import { DomainError } from '../errors.js';

/**
 * Scheduling state of a worker node. `Node` is the Control Plane's decision to use a
 * server as a worker; the server itself belongs to 2d2s (ADR-0002).
 */
export const NODE_SCHEDULING_STATES = ['active', 'cordoned', 'draining', 'offline'] as const;
export type NodeSchedulingState = (typeof NODE_SCHEDULING_STATES)[number];

export interface NodeResources {
  readonly totalMemoryMb: number;
  readonly totalDiskGb: number;
  readonly usedMemoryMb: number;
  readonly usedDiskGb: number;
}

export interface WorkerNode {
  readonly id: string;
  readonly serverExternalId: string;
  readonly schedulingState: NodeSchedulingState;
  readonly labels: ReadonlySet<string>;
  readonly resources: NodeResources;
  /** Capacity the scheduler may never hand out, so a node keeps room to recover. */
  readonly reservedMemoryMb: number;
  readonly reservedDiskGb: number;
  readonly lastHeartbeatAt: Date | null;
  readonly siteCount: number;
  readonly maxSites: number;
  /** What the node's agent last said it was; null before it has reported. */
  readonly agentVersion?: string | null;
}

/**
 * The first agent that runs template builds (ADR-0047): mounts a bundle and
 * applies a structure. An older one would deploy a site of a build without
 * its theme and plugins, so such a site goes only to a node with this or newer.
 */
export const TEMPLATE_BUILD_AGENT = '0.11.0';

/** Whether an agent's version is at least the one given; an unknown one is not. */
export function agentAtLeast(version: string | null | undefined, minimum: string): boolean {
  const parts = (value: string) => value.split('.').map((part) => Number.parseInt(part, 10) || 0);
  if (!version || !/^\d+(?:\.\d+){0,2}$/.test(version)) return false;
  const [have, want] = [parts(version), parts(minimum)];
  for (let index = 0; index < 3; index += 1) {
    if ((have[index] ?? 0) !== (want[index] ?? 0)) return (have[index] ?? 0) > (want[index] ?? 0);
  }
  return true;
}

/** A node is offline once heartbeats stop; the agent pulls, so silence is the signal. */
export const HEARTBEAT_TIMEOUT_MS = 120_000;

// Narrow parameters on purpose: a caller holding a projection of a node should not
// have to fabricate the fields a check does not read.
export function isHeartbeatStale(node: Pick<WorkerNode, 'lastHeartbeatAt'>, now: Date): boolean {
  if (!node.lastHeartbeatAt) return true;
  return now.getTime() - node.lastHeartbeatAt.getTime() > HEARTBEAT_TIMEOUT_MS;
}

/** Disk pressure thresholds. Past `critical` the node stops taking work at all. */
export const DISK_WARNING_RATIO = 0.8;
export const DISK_CRITICAL_RATIO = 0.9;

export function diskUsageRatio(resources: NodeResources): number {
  if (resources.totalDiskGb <= 0) return 1;
  return resources.usedDiskGb / resources.totalDiskGb;
}

export function availableMemoryMb(node: WorkerNode): number {
  return Math.max(0, node.resources.totalMemoryMb - node.resources.usedMemoryMb - node.reservedMemoryMb);
}

export function availableDiskGb(node: WorkerNode): number {
  return Math.max(0, node.resources.totalDiskGb - node.resources.usedDiskGb - node.reservedDiskGb);
}

const SCHEDULING_TRANSITIONS: Record<NodeSchedulingState, readonly NodeSchedulingState[]> = {
  active: ['cordoned', 'draining', 'offline'],
  cordoned: ['active', 'draining', 'offline'],
  // A drain must finish or be cancelled back to cordoned; going straight to active
  // would resume placement onto a node whose sites are still moving away.
  draining: ['cordoned', 'offline'],
  offline: ['active', 'cordoned'],
};

export function assertNodeSchedulingTransition(
  from: NodeSchedulingState,
  to: NodeSchedulingState,
): void {
  if (!SCHEDULING_TRANSITIONS[from].includes(to)) {
    throw new DomainError('INVALID_STATE_TRANSITION', `Node cannot move from ${from} to ${to}`, {
      from,
      to,
      allowed: SCHEDULING_TRANSITIONS[from],
    });
  }
}

/** A node may only be removed when it holds nothing (TZ section 10.5). */
export function assertNodeRemovable(node: Pick<WorkerNode, 'id' | 'siteCount'>): void {
  if (node.siteCount > 0) {
    throw new DomainError('INVALID_STATE_TRANSITION', 'Refusing to remove a node that still hosts sites', {
      nodeId: node.id,
      siteCount: node.siteCount,
    });
  }
}
